import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {LazyItemPicker} from '@github-ui/item-picker-base/LazyItemPicker'
import {useItemPickerErrorFallback} from '@github-ui/item-picker-base/useItemPickerErrorFallback'
import {
  LabelPickerBase,
  type CreateLabelMutationCallback,
  type LabelPickerBaseProps,
  type LabelPickerLabel as LabelPickerBaseLabel,
} from '@github-ui/item-picker-label'
import {QueryClient, useQueryClient} from '@github-ui/react-query'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  apiGetSearchLabels,
  buildItemPickerLabelSearchQueryKey,
  transformSearchLabelResponseToQueryData,
  processLabelNames,
  type LabelPickerQueryData,
} from './api/api-get-search-labels'
import {parseGraphQLResponse} from '@github-ui/item-picker-base/api/graphql'
import {useCreateLabelMutation} from './mutations/use-create-label-mutation'

//
// LabelPickerBase <== ItemPickerFetcher (internal) <== LabelPicker (exported)
//                                                  <== BulkIssueLabelPicker (exported)

export async function fetchLabels(
  queryClient: QueryClient,
  {
    owner,
    repo,
    query,
    count,
    labelNames,
  }: {
    owner: string
    repo: string
    query: string
    count?: number
    labelNames?: string[]
  },
): Promise<LabelPickerQueryData> {
  const response = await queryClient.fetchQuery({
    queryKey: buildItemPickerLabelSearchQueryKey({owner, repo, query, count, labelNames}),
    queryFn: async () => {
      const responseJson = await apiGetSearchLabels({owner, repo, query, count, labelNames})
      const responseData = parseGraphQLResponse(responseJson)
      return transformSearchLabelResponseToQueryData(responseData.data, labelNames)
    },
  })
  return response
}

export type LabelPickerLabel = LabelPickerBaseLabel

type PreloadedLabelData = {
  repositoryId: string
  labels: LabelPickerLabel[]
  totalCount: number
}

export type LabelPickerProps = Omit<LabelPickerBaseProps, 'filterItems'> & {
  labelNames?: string[]
  /**
   * Preloaded label data to eliminate initial network requests.
   *
   * When provided, the TSQ Label Picker will use this data instead of making
   * an initial fetch request, resulting in immediate rendering and improved
   * performance. This is particularly useful when the parent component
   * (like Relay wrappers) has already fetched the label data.
   *
   * The data should include:
   * - repositoryId: The repository identifier
   * - labels: Array of label objects with id, name, color, etc.
   * - totalCount: Total number of labels in the repository
   *
   * If not provided, the component will fetch labels using TanStack Query.
   */
  preloadedLabels?: PreloadedLabelData
}

function ItemPickerFetcher({
  repo,
  owner,
  labels,
  labelNames,
  preloadedLabels,
  noLabelOption,
  ...rest
}: LabelPickerProps) {
  const hasPreloadedData = preloadedLabels && !!preloadedLabels.labels?.length

  // Initialize state based on preloaded data availability
  const [isLoading, setIsLoading] = useState(!hasPreloadedData)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<LabelPickerQueryData | null>(() => {
    if (hasPreloadedData) {
      const labelsByNames = labelNames ? processLabelNames(labelNames, preloadedLabels.labels) : undefined

      return {
        repositoryId: preloadedLabels.repositoryId,
        labels: labelNames ? labelsByNames || [] : preloadedLabels.labels,
        totalCount: preloadedLabels.totalCount,
        labelsByNames,
      }
    }
    return null
  })

  const [initialLabels, setInitialLabels] = useState<LabelPickerLabel[]>(() => {
    if (hasPreloadedData && labelNames) {
      return processLabelNames(labelNames, preloadedLabels.labels)
    }
    return labels
  })

  const queryClient = useQueryClient()
  const {mutate: createLabelMutate} = useCreateLabelMutation()

  useEffect(() => {
    if (!hasPreloadedData) {
      const fetchInitialLabels = async () => {
        // Fetch the most recently updated labels in alphabetical order
        try {
          const response = await fetchLabels(queryClient, {
            owner,
            repo,
            query: '',
            labelNames,
          })
          setData(response)

          // If labelNames were provided, set the initial labels to the matched labels from the response
          if (labelNames && labelNames.length > 0) {
            setInitialLabels(response.labelsByNames || [])
          }

          setIsLoading(false)
          setIsError(false)
        } catch {
          setIsError(true)
          setIsLoading(false)
        }
      }
      // This useEffect is run to populate the initial list of labels to show in the picker.
      // It does not take a query into account and only relies on owner / repo
      // This useEffect has the `fetchInitialLabels` callback (which has `repo` and `owner`)
      // and `fetchKey` as its keys, so it will only be executed if one of those dependencies changes
      // - not whenever the filter changes.
      fetchInitialLabels()
    }
  }, [fetchKey, owner, queryClient, repo, labelNames, hasPreloadedData])

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('labels'),
    anchorElement: anchorProps => rest.anchorElement(anchorProps, anchorRef),
    open: true,
  })

  const fetchLabelsWithQueryClient = useCallback(
    async (query: string) => {
      const response = await fetchLabels(queryClient, {owner, repo, query})
      return response.labels
    },
    [queryClient, owner, repo],
  )

  const onCreate: CreateLabelMutationCallback = useCallback(
    (input, {onSuccess, onError}) => {
      createLabelMutate(input, {
        onSuccess,
        onError,
      })
    },
    [createLabelMutate],
  )

  // Handle noLabelOption for suggestions (add to the beginning of the list)
  const suggestions = useMemo(() => {
    const suggestionsList = data?.labels ?? []
    if (noLabelOption) {
      return [SPECIAL_VALUES.noLabelsData as LabelPickerLabel, ...suggestionsList]
    }
    return suggestionsList
  }, [data?.labels, noLabelOption])

  // Handle noLabelOption for selected labels (add when noLabelOption.selected is true)
  const allLabels = useMemo(() => {
    const labelsList = labelNames ? initialLabels : labels
    if (noLabelOption?.selected && !labelsList.find(l => l.id === SPECIAL_VALUES.noLabelsData.id)) {
      return [SPECIAL_VALUES.noLabelsData as LabelPickerLabel, ...labelsList]
    }
    return labelsList
  }, [initialLabels, labelNames, labels, noLabelOption?.selected])

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return (
    <LabelPickerBase
      repo={repo}
      owner={owner}
      repositoryId={data?.repositoryId ?? ''}
      labels={allLabels}
      suggestions={suggestions}
      totalNumberOfLabels={data?.totalCount ?? 0}
      isLoading={isLoading}
      setIsLoading={setIsLoading}
      searchLabels={fetchLabelsWithQueryClient}
      onCreate={onCreate}
      noLabelOption={noLabelOption}
      {...rest}
    />
  )
}

export function LabelPicker({
  repo,
  owner,
  anchorElement,
  readonly,
  labels,
  labelNames,
  noLabelOption,
  ...rest
}: LabelPickerProps) {
  return (
    <LazyItemPicker
      keybindingCommandId="item-pickers:open-labels"
      anchorElement={(anchorProps, ref) => anchorElement(anchorProps, ref)}
      createChild={() => (
        <ItemPickerFetcher
          repo={repo}
          owner={owner}
          readonly={readonly}
          initialFilter={''}
          labels={labels}
          labelNames={labelNames}
          triggerOpen
          anchorElement={anchorElement}
          noLabelOption={noLabelOption}
          {...rest}
        />
      )}
      insidePortal={rest.insidePortal}
    />
  )
}
